import { Injectable, Injector } from '@angular/core';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { AssetManagerInspection } from 'src/app/asset-manager/asset-manager-inspection';
import type { AssetManagerInspectionRetarget } from 'src/app/asset-manager/asset-manager-inspection-retarget';
import { Asset } from 'src/app/platform/busines-data/Framework/Asset';
import { AssetState, AssetStateList } from 'src/app/platform/busines-data/Framework/asset-state';
import { AssetStateCapture } from './asset-state-capture.service';
import { CameraControl } from './graphics/graphics-camera-controler.service';
import { GraphicsRenderRuler } from './graphics/graphics-render-ruler';
import { SceneGraphRenderer } from './scene-graph-renderer.service';

/**
 * AssetStateManager is the undo/redo orchestrator. It owns the history cursor
 * and delegates ALL rendering to SceneGraphRenderer.
 *
 * Responsibilities:
 * - undo() / redo(): move cursor, delegate to restoreState()
 * - pushState(): validate and add to history (single entry point)
 * - processAssetStateFromCopilot(): apply copilot state via SceneGraphRenderer
 * - clearHistory(): reset history on asset reload
 *
 * Phase 4 rewrite: slimmed from 215 lines / 8 dependencies to ~80 lines / 4 dependencies.
 * All format branching, mode rendering, and UI synchronization live in SceneGraphRenderer.
 */
@Injectable({ providedIn: 'root' })
export class AssetStateManager {

    public get asset(): Asset {
        return this.assetManager.asset;
    }

    constructor(
        private assetManager: AssetManager,
        private cameraControl: CameraControl,
        private sceneGraphRenderer: SceneGraphRenderer,
        private assetStateCapture: AssetStateCapture,
        private graphicsRenderRuler: GraphicsRenderRuler,
        private injector: Injector,
    ) {}

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    public undo(): void {
        // Before the canUndo guard: the retarget tool can show its region while the history is empty.
        if (this.endRetargetRegionSwitch()) return;
        if (!this.asset?.assetStateList?.canUndo) return;
        // End an armed retarget tool BEFORE the history moves: SceneGraphRenderer.syncUI would end it only
        // after reapplyCurrentViewMode(), and its cancel() would paint stale saved materials back on top.
        if (this.isRetargetArmed()) this.assetManager.setFunctionalityModeNone();

        // For Hide states, undo must toggle the CURRENT state's object (the one
        // being undone), not the previous state's. moveToPrevious() returns the
        // state we're moving TO, but we need the state we're moving FROM.
        const stateBeingUndone = this.asset.assetStateList.currentState;
        this.asset.assetStateList.moveToPrevious();

        if (stateBeingUndone?.action === 'Hide' && stateBeingUndone.object) {
            this.cameraControl.cancelAnimation();
            this.sceneGraphRenderer.renderHideState(this.asset, stateBeingUndone, 'undo');
            this.clearRulerOutsideSectionMode();
        } else {
            // For Select/root states, restore the state we moved to (the previous state).
            const targetState = this.asset.assetStateList.currentState;
            this.restoreState(targetState, 'undo');
        }
    }

    public redo(): void {
        if (this.endRetargetRegionSwitch()) return;
        if (!this.asset?.assetStateList?.canRedo) return;
        if (this.isRetargetArmed()) this.assetManager.setFunctionalityModeNone(); // see undo()

        // Mirror of undo(): a Hide state is a single-object toggle, not a
        // regions-map snapshot. Element hides are only represented by
        // state.object, so redo must go through renderHideState('redo') or
        // the element stays visible.
        const stateBeingRedone = this.asset.assetStateList.moveToNext();

        if (stateBeingRedone?.action === 'Hide' && stateBeingRedone.object) {
            this.cameraControl.cancelAnimation();
            this.sceneGraphRenderer.renderHideState(this.asset, stateBeingRedone, 'redo');
            this.clearRulerOutsideSectionMode();
        } else {
            this.restoreState(stateBeingRedone, 'redo');
        }
    }

    /**
     * Single entry point for adding states to history.
     * Performs validation (null check, duplicate detection) before delegating
     * to AssetStateList.addAssetState(). This encapsulates AssetStateList
     * entirely within AssetStateManager -- no direct access from outside.
     */
    public pushState(state: AssetState): void {
        if (!state || !this.asset?.assetStateList) return;

        const currentState = this.asset.assetStateList.currentState;
        if (currentState && this.assetStateCapture.isDuplicate(state, currentState)) {
            return;
        }

        this.asset.assetStateList.addAssetState(state);
    }

    /**
     * Applies a copilot-provided state by delegating to SceneGraphRenderer.
     * Does NOT add to history in Phase 4 -- Phase 5 will wire copilot to
     * call pushState() so copilot actions become undoable.
     */
    public processAssetStateFromCopilot(assetState: AssetState): void {
        if (!assetState) return;
        this.sceneGraphRenderer.renderState(this.asset, assetState);
        this.clearRulerOutsideSectionMode();
    }

    /**
     * Clears the undo/redo history. Called on asset reload to prevent
     * stale scene object references in old snapshots.
     */
    public clearHistory(): void {
        if (!this.asset) return;
        this.asset.assetStateList = new AssetStateList();
    }

    // -----------------------------------------------------------------------
    // Private
    // -----------------------------------------------------------------------

    /**
     * While the change-inspection-target tool shows a region it chose, undo/redo first mean "go back to
     * where the tool started and end it": that navigation is not in the history, so no entry is applied
     * and the index does not move. True when the key press was consumed.
     */
    private endRetargetRegionSwitch(): boolean {
        if (!this.retargetTool()?.returnToOriginAndEnd()) return false;
        this.asset?.assetStateList?.stateChanged.next(true);
        return true;
    }

    /** True while the change-inspection-target tool is armed (either pick step). */
    private isRetargetArmed(): boolean {
        const mode = this.assetManager.functionalityMode;
        return mode === 'INSPECSELECTCHANGE' || mode === 'INSPECCHANGETARGET';
    }

    /** Resolved lazily (like AssetManager's functionalityMode setter); null when unavailable. */
    private retargetTool(): AssetManagerInspectionRetarget | null {
        try {
            return this.injector.get(AssetManagerInspection).retarget ?? null;
        } catch {
            return null;
        }
    }

    /**
     * Unified restore pipeline for Select/root states, called by both undo()
     * and redo(). Cancels any in-flight camera animation, then delegates full
     * scene restoration to SceneGraphRenderer.renderState().
     *
     * Hide states never come here: undo() and redo() route them through
     * renderHideState(), which toggles the single hidden object.
     */
    private restoreState(state: AssetState | null, _direction: 'undo' | 'redo'): void {
        if (!state) return;

        // Cancel any in-flight camera animation (mid-animation undo/redo edge case)
        this.cameraControl.cancelAnimation();

        if (state.isRoot) {
            this.sceneGraphRenderer.renderRootState(this.asset);
            this.cameraControl.setCameraPosition(this.asset.box3);
        } else {
            this.sceneGraphRenderer.renderState(this.asset, state);
        }

        // Preserve production behavior: clear ruler geometries (and exit measure
        // tools) when restoring a state outside SECTIONS mode. SceneGraphRenderer
        // resets the functionality mode but does not clear ruler geometries.
        this.clearRulerOutsideSectionMode();
    }

    private restoreLightModelState(assetState: AssetState): boolean {
        if (!this.asset.isLightModel || assetState.mode !== 'MODEL' || !assetState.regions) {
            return false;
        }

        const lightRegions = this.asset.GetLightModelRegions();
        const hasLightRegionState = lightRegions.some(region => assetState.regions[region.Guid] !== undefined);
        if (!hasLightRegionState) {
            return false;
        }

        this.asset.lightCampaign?.setRegionsVisibility(assetState.regions);
        return true;
    }

    private clearRulerOutsideSectionMode(): void {
        if (this.asset?.mode === 'SECTIONS') {
            return;
        }

        this.graphicsRenderRuler.clearGeometries();
        if (this.assetManager.functionalityMode === 'MEASURETOOLS') {
            this.assetManager.setFunctionalityModeNone();
        }
    }
}
